import React, { useEffect } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Grid,
    Button,
} from "@mui/material";
import { Download as DownloadIcon, TableChart as ExcelIcon } from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import { statsStore, supplierStore, authStore } from "../stores";
import { FlightStat } from "../types";
import Constants from "../constants";
import { exportToExcelMultiSheet, formatDateForExcel } from "../utils/excelExport";
import { resolveStatsSupplierId } from "../utils/statsSupplier";

interface FlightStatsProps {
    clientId: number | null | undefined;
    startDate?: string;
    endDate?: string;
}

const FlightStats: React.FC<FlightStatsProps> = observer(({ clientId, startDate, endDate }) => {
    const { flightStats, isFlightLoading, flightError } = statsStore;
    const isClient = authStore.user && !["okko", "wink", "premiere", "admin"].includes(authStore.user.login);
    const isAdmin = authStore.user?.login === "admin";

    // Загружаем список поставщиков при монтировании
    useEffect(() => {
        if (supplierStore.suppliers.length === 0) {
            supplierStore.fetchSuppliers();
        }
    }, []);

    useEffect(() => {
        const supplierId = resolveStatsSupplierId(supplierStore.currentSupplier, authStore.user);
        if (startDate && endDate) {
            statsStore.fetchFlightStats(
                clientId,
                new Date(startDate),
                new Date(endDate),
                supplierId,
            );
        }
    }, [clientId, startDate, endDate, supplierStore.currentSupplier?.id, authStore.user?.login, authStore.user?.supplierId]);

    if (isFlightLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (flightError) {
        return <Alert severity="error">{flightError}</Alert>;
    }

    if (!flightStats || !flightStats.data || flightStats.data.length === 0) {
        return <Alert severity="info">Нет данных по рейсам за выбранный период.</Alert>;
    }

    const totalPageViews =
        flightStats.totalPageViews ?? flightStats.data.reduce((sum, flight) => sum + parseInt(flight.pageViews), 0);

    // Используем агрегаты с API (глобальные по периоду); сумма по строкам «Зрители» их не заменяет
    const totalViews =
        flightStats.totalViews ?? flightStats.data.reduce((sum, flight) => sum + parseInt(flight.totalViews), 0);
    const totalUniqueVisitors =
        flightStats.totalUniqueVisitors ??
        flightStats.data.reduce((sum, flight) => sum + parseInt(flight.uniqueVisitors), 0);
    const totalMediaViews =
        flightStats.totalMediaViews ?? flightStats.data.reduce((sum, flight) => sum + parseInt(flight.mediaViews), 0);

    // Расчёт просмотров медиа вне рейсов
    // Используем общие данные из mediaStats (все просмотры медиа) минус просмотры медиа на рейсах
    const mediaStatsAny = statsStore.mediaStats as any;
    // После нормализации в statsStore структура: {data: [], totalViewCount: number}
    const mediaData = Array.isArray(mediaStatsAny?.data) ? mediaStatsAny.data : [];
    const generalMediaViews = (mediaStatsAny?.totalViewCount ||
        mediaData.reduce((sum: number, media: any) => sum + parseInt(media.viewCount || "0"), 0) ||
        0) as number;
    const outsideMediaViews = Math.max(0, generalMediaViews - totalMediaViews);

    // Расчёт уникальных зрителей вне рейсов: общий distinct берём из API, сумма строк остаётся fallback.
    const generalUniqueViewers =
        (mediaStatsAny?.totalUniqueViewers ??
            mediaData.reduce((sum: number, media: any) => sum + parseInt(media.uniqueViewers || "0"), 0) ??
            0) as number;
    const outsideUniqueViewers = Math.max(0, generalUniqueViewers - totalUniqueVisitors);

    // Поставщик определяется в верхнем уровне (Dashboard) через supplierStore.currentSupplier

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" component="h2">
                    Статистика по рейсам
                </Typography>
                <Box display="flex" gap={1}>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                            const sep = Constants.CSV_SEPARATOR;
                            const csvEsc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;

                            const summaryBlock = [
                                csvEsc('СВОДКА (значения как на карточках дашборда)'),
                                [csvEsc('Показатель'), csvEsc('Значение')].join(sep),
                                [csvEsc('Период'), csvEsc(`${formatDateForExcel(new Date(startDate || ''))} — ${formatDateForExcel(new Date(endDate || ''))}`)].join(sep),
                                [csvEsc('Поставщик'), csvEsc(supplierStore.currentSupplier?.name || '—')].join(sep),
                                [csvEsc('Всего рейсов'), csvEsc(flightStats.data.length)].join(sep),
                                [csvEsc('Просмотры медиа (на рейсах)'), csvEsc(totalMediaViews)].join(sep),
                                [csvEsc('Просмотры медиа (рейс не указан)'), csvEsc(outsideMediaViews)].join(sep),
                                [csvEsc('Зрители (на рейсах)'), csvEsc(totalUniqueVisitors)].join(sep),
                                [csvEsc('Зрители (рейс не указан)'), csvEsc(outsideUniqueViewers)].join(sep),
                                ...(isAdmin
                                    ? [
                                          [csvEsc('Всего просмотров (по рейсам)'), csvEsc(totalViews)].join(sep),
                                          [csvEsc('Просмотры страниц (на рейсах)'), csvEsc(totalPageViews)].join(sep),
                                      ]
                                    : []),
                                '',
                                csvEsc('ДЕТАЛИЗАЦИЯ ПО РЕЙСАМ'),
                                csvEsc(
                                    'В таблице ниже столбец зрителей — уникальные только внутри строки рейса; итог «Зрители (на рейсах)» смотрите в блоке СВОДКА выше.',
                                ),
                                '',
                            ];

                            const csvHeaders = [
                                "Номер рейса",
                                "Откуда",
                                "Куда",
                                "Дата вылета",
                                "Клиент",
                                ...(isAdmin ? ["Устройство"] : []),
                                ...(isAdmin ? ["Всего просмотров"] : []),
                                "Зрители (только этот рейс)",
                                ...(isAdmin ? ["Просмотры страниц"] : []),
                                "Просмотры медиа",
                                "Первая активность",
                                "Последняя активность",
                            ];
                            const csvRows = flightStats.data.map((item: FlightStat) => [
                                item.flightNumber || "",
                                item.departureAirport || "",
                                item.arrivalAirport || "",
                                item.departureDate ? new Date(item.departureDate).toLocaleDateString("ru-RU") : "",
                                item.clientName || "",
                                ...(isAdmin ? [item.deviceName || ""] : []),
                                ...(isAdmin ? [item.totalViews || "0"] : []),
                                item.uniqueVisitors || "0",
                                ...(isAdmin ? [item.pageViews || "0"] : []),
                                item.mediaViews || "0",
                                item.firstActivity ? new Date(item.firstActivity).toLocaleString("ru-RU") : "",
                                item.lastActivity ? new Date(item.lastActivity).toLocaleString("ru-RU") : "",
                            ]);
                            const csvContent = [
                                ...summaryBlock,
                                csvHeaders.join(sep),
                                ...csvRows.map((row: any) => row.map((cell: any) => csvEsc(cell)).join(sep)),
                            ].join("\n");

                            // Добавляем BOM для правильного отображения кириллицы в Excel
                            const BOM = "\uFEFF";
                            const blob = new Blob([BOM + csvContent], {
                                type: "text/csv;charset=utf-8;",
                            });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `flight_stats_${new Date().toISOString().split("T")[0]}.csv`;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                        }}
                    >
                        Экспорт CSV
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<ExcelIcon />}
                        onClick={() => {
                            const headers = [
                                "Номер рейса",
                                "Откуда",
                                "Куда",
                                "Дата вылета",
                                "Клиент",
                                ...(isAdmin ? ["Устройство"] : []),
                                ...(isAdmin ? ["Всего просмотров"] : []),
                                "Зрители (только этот рейс)",
                                ...(isAdmin ? ["Просмотры страниц"] : []),
                                "Просмотры медиа",
                                "Первая активность",
                                "Последняя активность",
                            ];

                            const detailRows = flightStats.data.map((item: FlightStat) => [
                                item.flightNumber || "",
                                item.departureAirport || "",
                                item.arrivalAirport || "",
                                item.departureDate ? new Date(item.departureDate).toLocaleDateString("ru-RU") : "",
                                item.clientName || "",
                                ...(isAdmin ? [item.deviceName || ""] : []),
                                ...(isAdmin ? [item.totalViews || "0"] : []),
                                item.uniqueVisitors || "0",
                                ...(isAdmin ? [item.pageViews || "0"] : []),
                                item.mediaViews || "0",
                                item.firstActivity ? new Date(item.firstActivity).toLocaleString("ru-RU") : "",
                                item.lastActivity ? new Date(item.lastActivity).toLocaleString("ru-RU") : "",
                            ]);

                            const summarySheet: any[][] = [
                                ["Сводка за период (те же цифры, что на карточках экрана)", ""],
                                ["Период", `${formatDateForExcel(new Date(startDate || ""))} — ${formatDateForExcel(new Date(endDate || ""))}`],
                                ["Поставщик", supplierStore.currentSupplier?.name || "—"],
                                [],
                                ["Показатель", "Значение"],
                                ["Всего рейсов", flightStats.data.length],
                                ["Просмотры медиа (на рейсах)", totalMediaViews],
                                ["Просмотры медиа (рейс не указан)", outsideMediaViews],
                                ["Зрители (на рейсах)", totalUniqueVisitors],
                                ["Зрители (рейс не указан)", outsideUniqueViewers],
                                ...(isAdmin
                                    ? [
                                          ["Всего просмотров (по рейсам)", totalViews],
                                          ["Просмотры страниц (на рейсах)", totalPageViews],
                                      ]
                                    : []),
                                [],
                                [
                                    "Примечание",
                                    'Лист «Рейсы»: столбец зрителей — внутри одного рейса; итог на рейсах = строка «Зрители (на рейсах)» выше.',
                                ],
                            ];

                            exportToExcelMultiSheet({
                                filename: `flight_stats_${new Date().toISOString().split("T")[0]}`,
                                sheets: [
                                    { name: "Сводка", rows: summarySheet },
                                    { name: "Рейсы", rows: [headers, ...detailRows] },
                                ],
                            });
                        }}
                    >
                        Экспорт Excel
                    </Button>
                </Box>
            </Box>

            {/* Переключение поставщика выполняется табами на верхнем уровне (Dashboard) */}

            {/* Сводная статистика */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* 1) Всего рейсов */}
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Всего рейсов
                            </Typography>
                            <Typography variant="h4" color="primary">
                                {flightStats.data.length}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Уникальных рейсов
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {/* 2) Просмотры медиа (на рейсах) */}
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Просмотры медиа
                            </Typography>
                            <Typography variant="h4" color="warning.main">
                                {totalMediaViews.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                На рейсах
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {/* 3) Просмотры медиа (рейс не указан) */}
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Просмотры медиа
                            </Typography>
                            <Typography variant="h4" color="secondary">
                                {outsideMediaViews.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Рейс не указан
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {/* 4) Зрители (на рейсах) */}
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Зрители
                            </Typography>
                            <Typography variant="h4" color="success.main">
                                {totalUniqueVisitors.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                На рейсах
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {/* 5) Зрители (рейс не указан) */}
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Зрители
                            </Typography>
                            <Typography variant="h4" color="success.main">
                                {outsideUniqueViewers.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Рейс не указан
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Информационный баннер с пояснением - скрыт для поставщиков */}
            {/* <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Статистика по рейсам:</strong> Показывает только просмотры
            на устройствах во время рейсов. Общая статистика поставщика включает
            также просмотры вне рейсов (в аэропорту, на стоянке и т.д.).
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Важно:</strong> Зрители могут НЕ суммироваться (Общая ≠
            Рейсы + Вне рейсов), так как один пользователь может использовать
            устройства и на рейсе, и вне рейса.
          </Typography>
        </Alert> */}

            {/* Таблица статистики */}
            <Card>
                <CardContent>
                    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Номер рейса</TableCell>
                                    <TableCell>Откуда</TableCell>
                                    <TableCell>Куда</TableCell>
                                    <TableCell>Дата вылета</TableCell>
                                    <TableCell>Клиент</TableCell>
                                    {isAdmin && <TableCell>Устройство</TableCell>}
                                    {isAdmin && <TableCell align="right">Всего просмотров</TableCell>}
                                    <TableCell align="right">Зрители</TableCell>
                                    {isAdmin && <TableCell align="right">Просмотры страниц</TableCell>}
                                    <TableCell align="right">Просмотры медиа</TableCell>
                                    <TableCell align="right">Первая активность</TableCell>
                                    <TableCell align="right">Последняя активность</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {flightStats.data.map((flight: FlightStat, index: number) => (
                                    <TableRow key={index}>
                                        <TableCell>{flight.flightNumber}</TableCell>
                                        <TableCell>{flight.departureAirport}</TableCell>
                                        <TableCell>{flight.arrivalAirport}</TableCell>
                                        <TableCell>{new Date(flight.departureDate).toLocaleDateString()}</TableCell>
                                        <TableCell>{flight.clientName}</TableCell>
                                        {isAdmin && <TableCell>{flight.deviceName}</TableCell>}
                                        {isAdmin && (
                                            <TableCell align="right">
                                                {parseInt(flight.totalViews).toLocaleString()}
                                            </TableCell>
                                        )}
                                        <TableCell align="right">
                                            {parseInt(flight.uniqueVisitors).toLocaleString()}
                                        </TableCell>
                                        {isAdmin && (
                                            <TableCell align="right">
                                                {parseInt(flight.pageViews).toLocaleString()}
                                            </TableCell>
                                        )}
                                        <TableCell align="right">
                                            {parseInt(flight.mediaViews).toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right">
                                            {new Date(flight.firstActivity).toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right">
                                            {new Date(flight.lastActivity).toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
});

export default FlightStats;
